import React from "react";
import {
  Box,
  Chip,
  Sheet,
  Table,
  CircularProgress,
  Textarea,
  FormControl,
  FormLabel,
} from "@mui/joy";
import { SCreate, SBlock, SHistory, SDone, SEdit, CloseIcon } from "../../../styles/StyledMUI";
import {
  colorTokens,
  hrSheetSx,
  hrTableSx,
  hrAccentColor,
  textTokens,
} from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import DarkCard from "../../common/DarkCard";
import IconButtonCTA from "../../common/IconButtonCTA";
import { useBilling } from "../../../contexts/api/billing";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import type {
  CustomerProduct,
  CounterMovement,
  Prestation,
} from "../../../api/billing/Billing.typed";
import PurchaseProductModal from "./PurchaseProductModal";

type Props = {
  customerId: number;
  onCustomerUpdated?: () => void | Promise<void>;
};

const statusLabels: Record<
  string,
  { label: string; color: "success" | "warning" | "neutral" | "danger" }
> = {
  active: { label: "Actif", color: "success" },
  exhausted: { label: "Épuisé", color: "warning" },
  expired: { label: "Expiré", color: "neutral" },
  cancelled: { label: "Annulé", color: "danger" },
};

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const reasonLabels: Record<string, string> = {
  purchase: "Achat",
  excursion_validated: "Sortie",
  manual: "Ajustement",
  refund: "Remboursement",
  cancelled: "Annulation",
};

type PendingAdjust = { counterID: number };

export default function CustomerCountersTab({ customerId, onCustomerUpdated }: Props) {
  const {
    listCustomerCounters,
    listCounterMovements,
    cancelCounter,
    adjustCounter,
    listPrestations,
  } = useBilling();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const canEdit =
    hasPermission(Permission.BILLING_COUNTERS_EDIT) ||
    hasPermission(Permission.CUSTOMERS_PRODUCTS_EDIT);

  const [counters, setCounters] = React.useState<CustomerProduct[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [purchaseOpen, setPurchaseOpen] = React.useState(false);
  const [movements, setMovements] = React.useState<Record<number, CounterMovement[]>>({});
  const [expandedId, setExpandedId] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState<number | null>(null);

  const [prestationsMap, setPrestationsMap] = React.useState<Record<number, Prestation>>({});

  const [pending, setPending] = React.useState<PendingAdjust | null>(null);
  /** Chaîne éditée pour le nombre de crédits restants cible (solde absolu). */
  const [targetRemainingStr, setTargetRemainingStr] = React.useState("");
  const [adjustNote, setAdjustNote] = React.useState("");

  const reload = React.useCallback(async () => {
    setLoading(true);
    const data = await listCustomerCounters(customerId);
    if (data) setCounters(data);
    setLoading(false);
  }, [customerId, listCustomerCounters]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const handlePurchased = React.useCallback(() => {
    void reload().then(async () => {
      await onCustomerUpdated?.();
    });
  }, [reload, onCustomerUpdated]);

  React.useEffect(() => {
    listPrestations().then((data) => {
      if (!data) return;
      const map: Record<number, Prestation> = {};
      for (const p of data) map[p.ID] = p;
      setPrestationsMap(map);
    });
  }, [listPrestations]);

  const loadMovements = async (counterID: number) => {
    if (movements[counterID]) return;
    const data = await listCounterMovements(counterID);
    if (data) setMovements((prev) => ({ ...prev, [counterID]: data }));
  };

  const refreshMovements = async (counterID: number) => {
    const data = await listCounterMovements(counterID);
    if (data) setMovements((prev) => ({ ...prev, [counterID]: data }));
  };

  const handleToggle = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      loadMovements(id);
    }
  };

  const handleCancel = async (cpr: CustomerProduct) => {
    const name = cpr.Product?.Name ?? `Produit #${cpr.ProductID}`;
    const ok = await showConfirmation(
      "Annuler le produit",
      `Annuler le produit « ${name} » ? Cette action est irréversible.`,
    );
    if (!ok) return;
    await cancelCounter(cpr.ID);
    await reload();
  };

  const startAdjust = (cpr: CustomerProduct) => {
    setPending({ counterID: cpr.ID });
    setTargetRemainingStr(String(cpr.RemainingCount));
    setAdjustNote("");
  };

  const cancelAdjust = () => {
    setPending(null);
    setTargetRemainingStr("");
    setAdjustNote("");
  };

  const confirmAdjust = async (cpr: CustomerProduct) => {
    if (!pending || pending.counterID !== cpr.ID) return;
    const parsed = parseInt(targetRemainingStr.trim(), 10);
    if (Number.isNaN(parsed) || parsed < 0) return;
    const delta = parsed - cpr.RemainingCount;
    if (delta === 0) return;
    setBusy(pending.counterID);
    await adjustCounter(pending.counterID, { delta, note: adjustNote });
    if (expandedId === pending.counterID) await refreshMovements(pending.counterID);
    await reload();
    setBusy(null);
    setPending(null);
    setTargetRemainingStr("");
    setAdjustNote("");
  };

  return (
    <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DarkTypography level="title-md" colorVariant="title">
          Produits
        </DarkTypography>
        {canEdit && (
          <IconButtonCTA aria-label="Acheter un produit" onClick={() => setPurchaseOpen(true)}>
            <SCreate />
          </IconButtonCTA>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loading && counters.length === 0 && (
        <DarkTypography
          level="body-sm"
          sx={{ color: textTokens.color.dimmed, textAlign: "center", py: 2 }}
        >
          Aucun produit associé.
        </DarkTypography>
      )}

      {!loading &&
        counters.map((cpr) => {
          const status = statusLabels[cpr.Status] ?? {
            label: cpr.Status,
            color: "neutral" as const,
          };
          const isExpanded = expandedId === cpr.ID;
          const isBusy = busy === cpr.ID;
          const isActive = cpr.Status === "active";
          const hasPending = pending?.counterID === cpr.ID;
          const lines = cpr.Product?.Lines ?? [];

          let pendingAdjustPanel: React.ReactNode = null;
          if (hasPending && pending) {
            const parsed = parseInt(targetRemainingStr.trim(), 10);
            const hasValid = !Number.isNaN(parsed) && parsed >= 0;
            const delta = hasValid ? parsed - cpr.RemainingCount : null;
            const canApply = hasValid && delta !== 0;
            const deltaLabel =
              delta == null
                ? null
                : delta === 0
                  ? "Aucun changement"
                  : delta > 0
                    ? `+${delta} crédit${delta > 1 ? "s" : ""}`
                    : `−${-delta} crédit${-delta > 1 ? "s" : ""}`;

            pendingAdjustPanel = (
              <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <FormLabel sx={{ color: textTokens.color.dimmed }}>
                      Crédits restants (solde cible)
                    </FormLabel>
                    <DarkInput
                      type="number"
                      size="sm"
                      aria-label="Nouveau nombre de crédits restants"
                      slotProps={{
                        input: {
                          type: "number",
                          min: 0,
                          max: 999_999,
                          step: 1,
                          inputMode: "numeric",
                        },
                      }}
                      value={targetRemainingStr}
                      onChange={(e) => setTargetRemainingStr(e.target.value)}
                      onBlur={() => {
                        const t = parseInt(targetRemainingStr.trim(), 10);
                        if (Number.isNaN(t) || t < 0) setTargetRemainingStr("0");
                        else setTargetRemainingStr(String(t));
                      }}
                      sx={{ minHeight: 32 }}
                    />
                  </FormControl>
                  {deltaLabel != null && (
                    <DarkTypography
                      level="body-xs"
                      sx={{
                        color: canApply ? colorTokens.text.primary : textTokens.color.dimmed,
                        pb: 0.5,
                      }}
                    >
                      {deltaLabel}
                    </DarkTypography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Textarea
                    size="sm"
                    minRows={1}
                    maxRows={2}
                    placeholder="Note (optionnelle)"
                    value={adjustNote}
                    onChange={(e) => setAdjustNote(e.target.value)}
                    sx={{
                      flex: 1,
                      minWidth: 120,
                      backgroundColor: colorTokens.bg.surfaceMuted,
                      color: colorTokens.text.primary,
                      border: `1px solid ${colorTokens.border.strong}`,
                      borderRadius: "8px",
                      "&:focus-within": { borderColor: colorTokens.border.focus },
                      "& textarea": { color: colorTokens.text.primary },
                    }}
                  />
                  <IconButtonCTA
                    aria-label="Confirmer l'ajustement"
                    size="sm"
                    ctaVariant="success"
                    disabled={isBusy || !canApply}
                    onClick={() => void confirmAdjust(cpr)}
                  >
                    <SDone />
                  </IconButtonCTA>
                  <IconButtonCTA aria-label="Annuler" size="sm" onClick={cancelAdjust}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButtonCTA>
                </Box>
              </Box>
            );
          }

          return (
            <DarkCard key={cpr.ID} dense>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DarkTypography level="body-sm" sx={{ fontWeight: 600 }}>
                    {cpr.Product?.Name ?? `Produit #${cpr.ProductID}`}
                  </DarkTypography>
                  {lines.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                      {lines.map((l) => {
                        const prest = prestationsMap[l.PrestationID];
                        return (
                          <Chip
                            key={l.PrestationID}
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {prest
                              ? `${prest.Name} ×${l.Quantity}`
                              : `#${l.PrestationID} ×${l.Quantity}`}
                          </Chip>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                {cpr.IsUnlimited ? (
                  <Chip size="sm" color="primary" variant="soft">
                    Illimité
                  </Chip>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DarkTypography
                      level="body-sm"
                      colorVariant="primary"
                      sx={{ fontWeight: 700, minWidth: 40, textAlign: "center" }}
                    >
                      {cpr.RemainingCount}/{cpr.InitialCount}
                    </DarkTypography>
                    {canEdit && isActive && (
                      <IconButtonCTA
                        aria-label="Ajuster le solde des crédits"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => startAdjust(cpr)}
                      >
                        <SEdit />
                      </IconButtonCTA>
                    )}
                  </Box>
                )}

                <Chip size="sm" color={status.color} variant="soft">
                  {status.label}
                </Chip>
                <DarkTypography level="body-xs" sx={{ color: textTokens.color.dimmed }}>
                  {formatDate(cpr.PurchasedAt)}
                </DarkTypography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButtonCTA
                    aria-label="Historique"
                    size="sm"
                    onClick={() => handleToggle(cpr.ID)}
                  >
                    <SHistory />
                  </IconButtonCTA>
                  {canEdit && isActive && (
                    <IconButtonCTA
                      aria-label="Annuler"
                      size="sm"
                      ctaVariant="danger"
                      onClick={() => handleCancel(cpr)}
                    >
                      <SBlock />
                    </IconButtonCTA>
                  )}
                </Box>
              </Box>

              {pendingAdjustPanel}

              {isExpanded && (
                <Box sx={{ mt: 1.5 }}>
                  <DarkTypography level="title-sm" sx={{ mb: 0.5, color: hrAccentColor }}>
                    Historique
                  </DarkTypography>
                  <Sheet sx={hrSheetSx}>
                    <Table size="sm" sx={hrTableSx}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Mouvement</th>
                          <th>Raison</th>
                          <th>Par</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(movements[cpr.ID] ?? []).length === 0 && (
                          <tr>
                            <td colSpan={5}>
                              <DarkTypography
                                level="body-xs"
                                sx={{ color: textTokens.color.dimmed, textAlign: "center" }}
                              >
                                {movements[cpr.ID] ? "Aucun mouvement." : "Chargement..."}
                              </DarkTypography>
                            </td>
                          </tr>
                        )}
                        {(movements[cpr.ID] ?? []).map((m) => (
                          <tr key={m.ID}>
                            <td>{formatDate(m.CreatedAt)}</td>
                            <td>{m.Delta > 0 ? `+${m.Delta}` : m.Delta}</td>
                            <td>{reasonLabels[m.Reason] ?? m.Reason}</td>
                            <td>{m.CreatedByName || "—"}</td>
                            <td>{m.Note || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Sheet>
                </Box>
              )}
            </DarkCard>
          );
        })}

      <PurchaseProductModal
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        customerId={customerId}
        onPurchased={handlePurchased}
      />
    </Box>
  );
}
